<template>
	<div class="quote-header-container">
		<div class="quote-header">
			<template v-if="!emptyStockQuoteData">
				<i
					:class="['iconfont', 'icon-icon_xiangxia_16_xuanzekuang1', infoShow && 'rotate-icon']"
					@click="handleShowStockInfo"
				></i>
				<div class="price-box">
					<span :class="['stock-price', formatValueStyleFn]">{{ formatClosePrice() }}</span>
					<span
						:class="['stock-change', formatValueStyleFn]"
						v-for="(item, index) in mainData[0]"
						:key="index"
						>{{ item.value }}</span
					>
				</div>
				<div class="stock-info1">
					<div v-for="(item, index) in mainData[1]" :key="index" class="info1-item">
						<span class="stock-info-label">{{ item.label }}</span>
						<span class="stock-info-value">{{ item.value }}{{ item.unit }}</span>
					</div>
				</div>
			</template>
		</div>

		<el-collapse-transition>
			<div class="stock-info2" v-if="infoShow">
				<h4>{{ $t('enterprise.quotes-data') }}</h4>
				<div class="stock-info2-list">
					<div v-for="(item, index) in otherData" :key="index" class="list-item">
						<div v-for="(child, _index) in item" :key="_index" class="single-item">
							<span class="stock-info-label">{{ child.label }}</span
							><span
								:class="[
									'stock-info-value',
									child.prop == 'highPrice' && 'up',
									child.prop == 'lowPrice' && 'down'
								]"
								>{{ child.value }}{{ child.unit }}</span
							>
						</div>
					</div>
				</div>
			</div>
		</el-collapse-transition>
	</div>
</template>

<script setup>
import { ref, computed, getCurrentInstance, watch } from 'vue';
import { useStockVO } from '@/rebuild/hooks/useStockVO';
import { convertNumberToCNUnit, deepClone } from '@/utils/api';
import { EN, ZH } from '@/locales';

const props = defineProps({
	stockQuoteData: {
		type: Object,
		default: () => ({})
	}
});

const emptyStockQuoteData = computed(() => !props.stockQuoteData || Object.keys(props.stockQuoteData).length === 0);

const emit = defineEmits(['toggleInfo']);

const { isHKStock, isUSStock } = useStockVO();
const { proxy } = getCurrentInstance();

const infoShow = ref(false);
const mainData = ref([]);
const otherData = ref([]);

// 格式化收盘价
const formatClosePrice = () => {
	const { value } = convertNumberToCNUnit(props.stockQuoteData.closePrice);
	return value;
};

// 格式化涨跌样式
const formatValueStyleFn = computed(() => {
	if (proxy.$i18n.locale === ZH) {
		return props.stockQuoteData.change > 0 ? 'up' : 'down';
	} else {
		return props.stockQuoteData.change > 0 ? 'down' : 'up';
	}
});

// 初始化主要数据
const initMainData = result => {
	let rowArray = [
		[
			{
				label: proxy.$t(`enterprise.chg`), //涨跌额
				prop: 'change'
			},
			{
				label: proxy.$t(`enterprise.%Chg`), //涨跌幅
				prop: 'changePct'
			}
		],
		[
			{
				label: proxy.$t(`enterprise.P/B`), //市净率
				prop: 'pb'
			},
			{
				label: proxy.$t(`enterprise.P/E`), //市营率
				prop: 'peTtm'
			}
		],
		[
			{
				label: proxy.$t(`enterprise.high`), //最高
				prop: 'highPrice'
			},
			{
				label: proxy.$t(`enterprise.low`), //最低
				prop: 'lowPrice'
			}
		],
		[
			{
				label: proxy.$t(`enterprise.volume`), //成交量
				prop: isHKStock.value ? 'turnoverVolumeShares' : 'turnoverVolume'
			},
			{
				label: proxy.$t(`enterprise.turnover`), //成交额
				prop: 'turnoverValue'
			}
		],
		[
			{
				label: proxy.$t(`enterprise.float-cap`), //流通值
				prop: 'negotiableMv'
			},
			{
				label: proxy.$t(`enterprise.market-cap`), //总市值
				prop: 'totalMv'
			}
		]
	];

	if (isUSStock.value) {
		rowArray[1] = [
			{
				label: proxy.$t(`enterprise.P/E`), //市营率
				prop: 'peTtm'
			}
		];
		rowArray[3] = [
			{
				label: proxy.$t(`enterprise.volume`), //成交量
				prop: 'turnoverVolumeShares'
			},
			{
				label: proxy.$t(`enterprise.market-cap`), //总市值
				prop: 'totalMv'
			}
		];
		delete rowArray[4];
	}

	const formatValueFn = (propName, value) => {
		if (propName == 'changePct') {
			return `${value}%`;
		}
		return value;
	};

	const formatUnitFn = (propName, unit) => {
		if (propName === 'turnoverVolume') {
			return proxy.$i18n.locale === EN ? `${unit}` : `${unit}手`;
		}
		if (propName == 'turnoverVolumeShares') {
			return proxy.$i18n.locale === EN ? `${unit}` : `${unit}股`;
		}
		return unit;
	};

	mainData.value = rowArray.map(item => {
		return item.map(childItem => {
			// 中文时可能会使用万手，切换至英文时需要将手置换为股 - A股 一手固定100股
			if (proxy.$i18n.locale === EN && childItem.prop === 'turnoverVolume') {
				result[childItem.prop] *= 100; // 将 turnoverVolume  单位由手转换为股
			}

			const { value, unit } = convertNumberToCNUnit(result[childItem.prop]);

			return {
				label: childItem.label,
				value: formatValueFn(childItem.prop, value),
				unit: formatUnitFn(childItem.prop, unit),
				prop: childItem.prop
			};
		});
	});

	otherData.value = mainData.value.slice(2);
};

const handleShowStockInfo = () => {
	infoShow.value = !infoShow.value;
	emit('toggleInfo', infoShow.value);
};

// 监听外部传入的数据变化
watch(
	() => props.stockQuoteData,
	newData => {
		const copyData = deepClone(newData);
		if (copyData && Object.keys(copyData).length > 0) {
			initMainData(copyData);
		}
	},
	{ deep: true, immediate: true }
);

defineExpose({
	handleShowStockInfo
});
</script>

<style lang="less" scoped>
.quote-header-container {
	position: relative;
}

.quote-header {
	position: relative;
	width: 100%;
	padding: 0 16px;
	z-index: 20;
	height: 88px;
	display: flex;
	flex-direction: column;
	justify-content: center;
	background: #fff;

	.icon-icon_xiangxia_16_xuanzekuang1 {
		position: absolute;
		top: 24px;
		right: 16px;
		font-size: 24px;
		color: #c9cdd4;
		transition: all 0.3s;
		cursor: pointer;
	}

	.rotate-icon {
		transform: rotate(180deg);
	}

	.price-box {
		padding-bottom: 8px;
		display: flex;
		align-items: baseline;
		justify-content: flex-start;
		font-family: 'DIN Alternate';
		gap: 8px;

		.stock-price {
			font-size: 28px;
			font-weight: 700;
			line-height: 36px;
		}

		.stock-change {
			font-size: 14px;
			font-weight: 700;
			line-height: 24px;
		}
	}

	.stock-info1 {
		display: flex;
		justify-content: flex-start;
		gap: 12px;
	}

	.stock-info-label {
		padding-right: 8px;
		color: var(--t-secondary-color, #4e5969);
		font-size: 12px;
		font-weight: 400;
		line-height: 16px;
	}

	.stock-info-value {
		color: var(--t-emphasis-color, #1d2129);
		font-family: 'DIN Alternate';
		font-size: 14px;
		font-weight: 700;
		line-height: 18px;
	}
}

.stock-info2 {
	position: absolute;
	left: 0;
	right: 0;
	top: 88px;
	z-index: 21;
	padding: 0 16px 12px 16px;
	background: #fff;

	h4 {
		padding-top: 12px;
		color: var(--t-emphasis-color, #1d2129);
		font-size: 14px;
		font-weight: 500;
		line-height: 18px;
	}

	.stock-info2-list {
		display: flex;
		flex-wrap: wrap;

		.list-item {
			width: 50%;
			flex-shrink: 0;
			margin-top: 16px;

			&:nth-child(even) {
				padding-left: 16px;
			}

			&:nth-child(odd) {
				padding-right: 16px;
				border-right: 1px solid #f2f3f5;
			}

			.single-item {
				display: flex;
				justify-content: space-between;
				align-items: center;

				&:first-child {
					padding-bottom: 8px;
				}
			}
		}
	}
}

.up {
	color: #f23d3d;
}

.down {
	color: #30bf54;
}
</style>
